import { Injectable } from '@angular/core'
import { RemoteData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store'
import { memoize } from 'lodash-es'
import { FeatureFlagKeyOfType, LaunchDarklyFlagValue, WebFeatureFlags } from '../feature-flags'
import { SetFeatureFlagValues } from './feature-flag.actions'
import { FeatureFlagStateModel } from './feature-flag.model'

export function defaultFeatureFlagState(): FeatureFlagStateModel {
  return {
    featureFlags: {
      'test-boolean-flag': false,
      'facebook-sso': false,
      'recording-vault-support': false,
      'recording-vault-activity-log-liveness': false,
      'disable-chromium-remapping-hack': false,
      'feature-disable-channel-remapping': false,
      'real-time-stt-attribution-approach': 'never-use-software-based-attribution',
      'design-system-overlay': false,
      'detailed-error-messages': false,
      'rc-2115-display-timezone-conditions-on-the-real-time-playback-page': false,
      'rth-feature-show-infrastructure-status': false,
      'offline-real-time-recordings': false,
      'annotations-first-release': false,
      'audit-export-button': false,
      'search-everything-by-default': false,
      'release-modifying-sessions': false,
      'court-system-selector': false,
      'restrict-sealed-segments-by-on-the-record-times': false,
      'release-refresh-playback-on-playlist-change': false,
      'navigation-redesign': false,
      'use-signalled-rtstt-component': false,
    },
    hasInitializedNonDefaultFlagValues: false,
  }
}

@State<FeatureFlagStateModel>({
  name: 'featureFlagState',
  defaults: defaultFeatureFlagState(),
})
@Injectable()
export class FeatureFlagState {
  @Selector()
  static allFeatureFlags(state: FeatureFlagStateModel): WebFeatureFlags {
    return state.featureFlags
  }

  static featureFlag = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      featureFlag: FeatureFlagKeyOfType<T>,
    ): ((featureFlags: ReturnType<typeof FeatureFlagState.allFeatureFlags>) => T) => {
      return createSelector(
        [FeatureFlagState.allFeatureFlags],
        featureFlags => featureFlags[featureFlag as keyof WebFeatureFlags] as T,
      )
    },
  )

  static resolvedFeatureFlagValue = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      featureFlag: FeatureFlagKeyOfType<T>,
    ): ((state: FeatureFlagStateModel) => RemoteData<T>) => {
      return createSelector([FeatureFlagState], state => {
        if (!state.hasInitializedNonDefaultFlagValues) {
          return RemoteData.loading()
        }
        return RemoteData.success(state.featureFlags[featureFlag as keyof WebFeatureFlags] as T)
      })
    },
  )

  @Action(SetFeatureFlagValues)
  setFeatureFlagValues(
    { patchState, getState }: StateContext<FeatureFlagStateModel>,
    { featureFlags, hasInitializedNonDefaultFlagValues }: SetFeatureFlagValues,
  ): void {
    const { featureFlags: currentFlags } = getState()
    const mergeFlags = <T extends LaunchDarklyFlagValue>(flagName: FeatureFlagKeyOfType<T>): T =>
      (featureFlags[flagName as keyof WebFeatureFlags] as T | undefined) ??
      (currentFlags[flagName as keyof WebFeatureFlags] as T)

    patchState({
      featureFlags: {
        'test-boolean-flag': mergeFlags('test-boolean-flag'),
        'recording-vault-support': mergeFlags('recording-vault-support'),
        'facebook-sso': mergeFlags('facebook-sso'),
        'recording-vault-activity-log-liveness': mergeFlags('recording-vault-activity-log-liveness'),
        'disable-chromium-remapping-hack': mergeFlags('disable-chromium-remapping-hack'),
        'feature-disable-channel-remapping': mergeFlags('feature-disable-channel-remapping'),
        'real-time-stt-attribution-approach': mergeFlags('real-time-stt-attribution-approach'),
        'design-system-overlay': mergeFlags('design-system-overlay'),
        'detailed-error-messages': mergeFlags('detailed-error-messages'),
        'rc-2115-display-timezone-conditions-on-the-real-time-playback-page': mergeFlags(
          'rc-2115-display-timezone-conditions-on-the-real-time-playback-page',
        ),
        'rth-feature-show-infrastructure-status': mergeFlags('rth-feature-show-infrastructure-status'),
        'offline-real-time-recordings': mergeFlags('offline-real-time-recordings'),
        'annotations-first-release': mergeFlags('annotations-first-release'),
        'audit-export-button': mergeFlags('audit-export-button'),
        'search-everything-by-default': mergeFlags('search-everything-by-default'),
        'release-modifying-sessions': mergeFlags('release-modifying-sessions'),
        'court-system-selector': mergeFlags('court-system-selector'),
        'restrict-sealed-segments-by-on-the-record-times': mergeFlags(
          'restrict-sealed-segments-by-on-the-record-times',
        ),
        'release-refresh-playback-on-playlist-change': mergeFlags('release-refresh-playback-on-playlist-change'),
        'navigation-redesign': mergeFlags('navigation-redesign'),
        'use-signalled-rtstt-component': mergeFlags('use-signalled-rtstt-component'),
      },
      hasInitializedNonDefaultFlagValues,
    })
  }
}
